import { Contract } from './types';

/**
 * Verifica se há sobreposição de datas entre contratos de um mesmo creator
 * @param contracts Lista de contratos existentes
 * @param creatorId ID do creator
 * @param startDate Data de início do novo contrato
 * @param endDate Data de fim do novo contrato
 * @param excludeContractId ID do contrato a excluir da validação (caso esteja editando)
 * @returns true se houver sobreposição, false caso contrário
 */
export const hasContractOverlap = (
  contracts: Contract[],
  creatorId: string,
  startDate: Date,
  endDate: Date,
  excludeContractId?: string
): boolean => {
  return contracts.some((contract) => {
    // Ignora o contrato sendo editado
    if (excludeContractId && contract.id === excludeContractId) {
      return false;
    }

    // Ignora contratos deletados
    if (contract.deletedAt) {
      return false;
    }

    // Verifica se é o mesmo creator
    if (contract.creatorId !== creatorId) {
      return false;
    }

    const contractStart = new Date(contract.startDate);
    const contractEnd = new Date(contract.endDate);

    // Verifica sobreposição de datas
    return (
      (startDate >= contractStart && startDate <= contractEnd) ||
      (endDate >= contractStart && endDate <= contractEnd) ||
      (startDate <= contractStart && endDate >= contractEnd)
    );
  });
};

/**
 * Valida se a data de fim é posterior à data de início
 */
export const isEndDateValid = (startDate: Date, endDate: Date): boolean => {
  return endDate > startDate;
};

/**
 * Valida se o arquivo tem extensão permitida (apenas PDF)
 */
export const isValidFileType = (file: File): boolean => {
  const allowedTypes = ['application/pdf'];

  if (allowedTypes.includes(file.type)) {
    return true;
  }

  if (file.name) {
    return file.name.toLowerCase().endsWith('.pdf');
  }

  return false;
};

import { MAX_FILE_SIZE_BYTES, isValidFileSize as validateFileSize } from "@/shared/lib/fileUploadConstants";

/**
 * Valida se o arquivo não excede o tamanho máximo (500KB)
 * @deprecated Use isValidFileSize de @/shared/lib/fileUploadConstants
 */
export const isValidFileSize = (file: File, _maxSizeMB?: number): boolean => {
  return validateFileSize(file);
};

const removeDiacritics = (value: string): string =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * Sanitiza o nome do arquivo para um formato seguro
 */
export const sanitizeFileName = (fileName: string): string => {
  const trimmed = fileName.trim();
  const fallbackBase = `contrato-${Date.now()}`;

  if (!trimmed) {
    return `${fallbackBase}.pdf`;
  }

  const lastDotIndex = trimmed.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0 && lastDotIndex < trimmed.length - 1;

  let base = hasExtension ? trimmed.slice(0, lastDotIndex) : trimmed;
  let extension = hasExtension ? trimmed.slice(lastDotIndex + 1) : '';

  base = removeDiacritics(base)
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();

  extension = removeDiacritics(extension)
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase();

  if (!base) {
    base = fallbackBase;
  }

  if (!extension) {
    return base;
  }

  return `${base}.${extension}`;
};
